import React, { useMemo } from 'react';
import ReactFlow, { Background, Controls, MarkerType, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import { useJob } from '../data/firestore';

// Live React-Flow visualization of an ephemeral fan-out job: the always-on
// orchestrator launches a `prep` task (download .RDC once to shared EFS), N
// `chunk` tasks (one per frame range, decoded+encoded in parallel on separate
// Fargate tasks), and a `stitch` task that concatenates the parts and uploads
// the HLS proxy / master to S3+CDN. Each node shows a progress ring + live fps /
// frames / vCPU and is colored by status; edges animate while work is flowing.

const STATUS_COLOR = {
  pending: '#5b6b82',
  launching: '#5b8def',
  running: '#14e0c8',
  done: '#3ddc84',
  error: '#ff6b6b',
};
const colorFor = (t) => STATUS_COLOR[t?.status] || STATUS_COLOR.pending;

function ProgressRing({ frac = 0, color = '#14e0c8', size = 34 }) {
  const r = (size - 5) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(1, frac)));
  return (
    <svg width={size} height={size} className="cs-job-ring" viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="3"
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="52%" dominantBaseline="middle" textAnchor="middle" fontSize="9" fill="var(--text)">
        {Math.round((frac || 0) * 100)}
      </text>
    </svg>
  );
}

function TaskCard({ title, task, hint }) {
  const frac = task?.status === 'done' ? 1 : task?.progress || 0;
  const color = colorFor(task);
  return (
    <div className="cs-job-card">
      <ProgressRing frac={frac} color={color} />
      <div className="cs-job-card-body">
        <div className="cs-job-card-title">{title}</div>
        <div className="cs-job-card-meta">
          <span className="cs-job-stage" style={{ color }}>{task?.stage || task?.status || 'pending'}</span>
          {task?.fps ? <span>{Math.round(task.fps)} fps</span> : null}
          {task?.framesDone != null && task?.framesTotal
            ? <span>{task.framesDone}/{task.framesTotal}f</span>
            : null}
          {task?.vcpu ? <span>{task.vcpu} vCPU</span> : null}
        </div>
        {hint ? <div className="cs-job-card-hint">{hint}</div> : null}
      </div>
    </div>
  );
}

function plainNode(id, x, y, color, label, width = 150) {
  return {
    id,
    position: { x, y },
    data: { label },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      width,
      background: 'var(--bg-elev)',
      color: 'var(--text)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderLeft: `4px solid ${color}`,
      borderRadius: 10,
      padding: '6px 8px',
      fontSize: 11,
      textAlign: 'left',
    },
  };
}

export default function JobFanoutGraph({ jobId, height = 360, title }) {
  const { job, tasks } = useJob(jobId);

  const chunkTasks = useMemo(() => tasks.filter((t) => t.role === 'chunk'), [tasks]);
  const prepTask = tasks.find((t) => t.role === 'prep');
  const stitchTask = tasks.find((t) => t.role === 'stitch');

  const { nodes, edges } = useMemo(() => {
    if (!job) return { nodes: [], edges: [] };
    const n = [];
    const e = [];
    const rows = Math.min(8, Math.max(1, chunkTasks.length || (job.chunks || 1)));
    const orchColor = job.status === 'error' ? STATUS_COLOR.error : job.status === 'done' ? STATUS_COLOR.done : STATUS_COLOR.running;

    n.push(plainNode('orch', 0, (rows * 64) / 2 - 30, orchColor, (
      <div className="cs-job-card">
        <div className="cs-job-card-body">
          <div className="cs-job-card-title">Orchestrator</div>
          <div className="cs-job-card-meta"><span>{job.status}</span><span>cap {job.maxConcurrency || 32}</span></div>
        </div>
      </div>
    ), 160));

    n.push(plainNode('prep', 210, (rows * 64) / 2 - 30, colorFor(prepTask), (
      <TaskCard title="Prep · download .RDC" task={prepTask} hint="shared EFS scratch" />
    ), 180));

    const count = chunkTasks.length || job.chunks || 0;
    const cols = Math.max(1, Math.ceil(count / rows));
    const slots = count
      ? chunkTasks
      : Array.from({ length: job.chunks || 0 }, (_, i) => ({ index: i, role: 'chunk', status: 'pending' }));
    slots.forEach((t, i) => {
      const col = Math.floor(i / rows);
      const row = i % rows;
      const id = `chunk-${t.index ?? i}`;
      n.push(plainNode(id, 450 + col * 200, row * 64, colorFor(t), (
        <TaskCard title={`chunk ${t.index ?? i}`} task={t} />
      ), 180));
      e.push({
        id: `prep-${id}`, source: 'prep', target: id, type: 'smoothstep',
        animated: t.status === 'running' || t.status === 'launching',
        style: { stroke: colorFor(t), strokeWidth: 1.4, opacity: 0.7 },
        markerEnd: { type: MarkerType.ArrowClosed, color: colorFor(t) },
      });
      e.push({
        id: `${id}-stitch`, source: id, target: 'stitch', type: 'smoothstep',
        animated: t.status === 'done' && (stitchTask?.status === 'running' || stitchTask?.status === 'launching'),
        style: { stroke: t.status === 'done' ? STATUS_COLOR.done : 'rgba(255,255,255,0.18)', strokeWidth: 1.2, opacity: 0.6 },
        markerEnd: { type: MarkerType.ArrowClosed, color: t.status === 'done' ? STATUS_COLOR.done : 'rgba(255,255,255,0.18)' },
      });
    });

    const stitchX = 450 + cols * 200 + 30;
    n.push(plainNode('stitch', stitchX, (rows * 64) / 2 - 40, colorFor(stitchTask), (
      <TaskCard title="Stitch · concat -c copy" task={stitchTask} hint="HLS / master" />
    ), 180));

    const outColor = job.status === 'done' ? STATUS_COLOR.done : 'rgba(255,255,255,0.18)';
    n.push(plainNode('out', stitchX + 210, (rows * 64) / 2 - 30, outColor, (
      <div className="cs-job-card">
        <div className="cs-job-card-body">
          <div className="cs-job-card-title">S3 + CDN</div>
          <div className="cs-job-card-meta"><span>{job.type === 'r3d-master' ? 'master.mp4' : 'HLS proxy'}</span></div>
        </div>
      </div>
    ), 150));

    e.push({ id: 'orch-prep', source: 'orch', target: 'prep', type: 'smoothstep', animated: !!prepTask && prepTask.status !== 'done', style: { stroke: 'rgba(255,255,255,0.25)', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed } });
    e.push({ id: 'stitch-out', source: 'stitch', target: 'out', type: 'smoothstep', animated: stitchTask?.status === 'running', style: { stroke: outColor, strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: outColor } });

    return { nodes: n, edges: e };
  }, [job, chunkTasks, prepTask, stitchTask]);

  if (job === undefined) {
    return <div className="cs-job-graph-empty">Connecting to job…</div>;
  }
  if (job === null) {
    return <div className="cs-job-graph-empty">No active fan-out job.</div>;
  }

  const running = tasks.filter((t) => t.status === 'running').length;
  const done = chunkTasks.filter((t) => t.status === 'done').length;
  const pct = Math.round((job.progress || 0) * 100);

  return (
    <div className="cs-job-graph">
      <div className="cs-job-head">
        <div>
          <strong>{title || 'Ephemeral fan-out'}</strong>
          <span className={`cs-job-badge cs-job-badge-${job.status}`}>{job.stage || job.status}</span>
        </div>
        <div className="cs-job-stats">
          <span>{pct}%</span>
          <span>{done}/{job.chunks || chunkTasks.length || 0} chunks</span>
          <span>{running} task{running === 1 ? '' : 's'} live</span>
          {job.vcpuPerTask ? <span>{running * job.vcpuPerTask} vCPU active</span> : null}
        </div>
      </div>
      <div className="cs-job-bar"><i style={{ width: `${pct}%` }} /></div>
      <div className="cs-job-flow" style={{ height }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          fitView
          fitViewOptions={{ padding: 0.1 }}
          nodesConnectable={false}
          nodesDraggable={false}
          edgesFocusable={false}
          proOptions={{ hideAttribution: true }}
          minZoom={0.2}
          maxZoom={1.5}
        >
          <Background color="rgba(255,255,255,0.06)" gap={20} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
      {job.status === 'error' && job.message ? (
        <div className="cs-job-err">⚠ {job.message}</div>
      ) : null}
    </div>
  );
}
